import { useState, useEffect, useContext } from 'react';

import { getAllTasks, updateTask, deleteTask } from 'api/tasks';
import { getAllUsersWithNursesAndAdmins } from 'api/users';
import { getAllDoctors } from 'api/doctors';

import { filtersToQueryString, initialFilters } from 'helpers/filtering';

import Table from 'components/table/table';
import { COLUMNS } from 'config/task';
import { setOwnerOptions } from 'helpers/owner';
import { setDoctorOptions } from 'helpers/doctor';

import Modal from 'components/modal/delete';

import { UserContext } from 'contexts/user';

const Tasks = ({ history, onCountUpdate }) => {
  const [fetching, setFetching] = useState(true);
  const [allTasks, setAllTasks] = useState();
  const [meta, setMeta] = useState({});
  const [columns, setColumns] = useState(COLUMNS);
  const [owners, setOwners] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState();

  const context = useContext(UserContext);

  const fetchData = async (page, filters = {}, search) => {
    const filtersString = filtersToQueryString(filters, search);
    const data = await getAllTasks(page, filtersString);

    setAllTasks(data.items);
    setMeta(data.meta);
    setFetching(false);
  };

  useEffect(() => {
    (async () => {
      const allOwners = await getAllUsersWithNursesAndAdmins();
      const allDoctors = await getAllDoctors();

      const newColumns = setOwnerOptions({
        user: context.user,
        columns: COLUMNS,
        data: allOwners,
      });

      const newColumnsWithDocs = setDoctorOptions({
        columns: newColumns,
        data: allDoctors,
      });

      const initFilters = initialFilters(newColumnsWithDocs);
      await fetchData(1, initFilters);

      setColumns(newColumnsWithDocs);
      setOwners(allOwners);
      setDoctors(allDoctors);
      setFetching(false);
    })();
  }, [columns, context.user]);

  const onStatusUpdate = async (row) => {
    const id = allTasks.findIndex((t) => t.id === row.id);

    const newStatus =
      allTasks[id].status === 'complete' ? 'incomplete' : 'complete';

    allTasks[id].status = newStatus;
    await updateTask(row.id, { ...row, status: newStatus });

    setAllTasks([...allTasks]);
    onCountUpdate();
  };

  const onUpdate = async (prop, value, c) => {
    const id = allTasks.findIndex((t) => t.id === c.id);
    const list = prop === 'owner' ? owners : doctors;

    allTasks[id][prop] = list.find((o) => o.id === value);

    await updateTask(c.id, {
      ...c,
      [prop]: value,
    });

    setAllTasks([...allTasks]);
    onCountUpdate();
  };

  const onTextUpdate = async (value, row, col) => {
    const id = allTasks.findIndex((t) => t.id === row.id);

    allTasks[id][col.accessor] = value;

    await updateTask(row.id, {
      ...row,
      [col.accessor]: value,
    });

    setAllTasks([...allTasks]);
  };

  const onDateUpdate = async (date, c) => {
    const id = allTasks.findIndex((t) => t.id === c.id);
    const newDate = new Date(date.toDateString()); // remove time

    allTasks[id].dueDate = newDate;

    await updateTask(c.id, {
      ...c,
      dueDate: newDate,
    });

    setAllTasks([...allTasks]);
    onCountUpdate();
  };

  const onDelete = async () => {
    const newTasks = allTasks.filter((t) => t.id !== currentTask.id);

    await deleteTask(currentTask.id);

    setAllTasks([...newTasks]);
    onCountUpdate();
    setDeleteModalOpen(false);
  };

  return (
    <>
      <main className="th-main">
        <section className="th-canvas">
          <header className="th-canvas-header post-header">
            <h2 className="th-canvas-title" data-test-screen-title>
              <span className="active">Tasks</span>
            </h2>
            <section className="view-actions">
              <button
                className="btn-green"
                data-cy="submit"
                onClick={() => history.push('/tasks/add')}
              >
                Add task
              </button>
            </section>
          </header>
          <section className="view-container">
            <Table
              columns={columns}
              data={allTasks}
              meta={meta}
              hasViewBar
              fetching={fetching}
              fetchData={(p, f, s) => fetchData(p, f, s)}
              onSelect={(accessor, value, row) =>
                onUpdate(accessor, value, row)
              }
              onStatusUpdate={(row, col) => onStatusUpdate(row, col)}
              onSingleDatePick={(date, row) => onDateUpdate(date, row)}
              onTextUpdate={(value, row, col) => onTextUpdate(value, row, col)}
              onDelete={(row) => {
                setDeleteModalOpen(true);
                setCurrentTask(row);
              }}
            />
          </section>
        </section>
      </main>
      {deleteModalOpen && (
        <Modal
          title="Task delete"
          content="Are you sure you want to delete this task?"
          onClose={() => setDeleteModalOpen(false)}
          onDelete={() => onDelete()}
        />
      )}
    </>
  );
};

export default Tasks;
